import { BrainProvisioningStatus } from "@/components/BrainProvisioningStatus";
import { useSetupConditions } from "@/hooks/useSetupConditions";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar-context";
import { api } from "@/lib/api";
import { useSuperAdminView } from "@/lib/use-superadmin-view";
import { relativeTime } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import {
  ArrowLeft,
  Container as ContainerIcon,
  EllipsisVertical,
  KeyRound,
  Loader2,
  Play,
  Square,
  Trash2,
} from "lucide-react";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { AccessNotices } from "@/components/boo/AccessNotices";
import { BooNameEditor } from "@/components/boo/BooNameEditor";
import { BooNoAccess, BooSpendOnlyNotice } from "@/components/boo/BooNoAccess";
import { ConnectorSetupPage } from "@/pages/container-tabs/integrations/connectors/ConnectorSetupPage";
import { TakeOwnershipDialog } from "@/components/boo/TakeOwnershipDialog";
import { UsageTab } from "@/pages/container-tabs/UsageTab";
import { containerPermissions } from "@/lib/boo-permissions";
import { useIdentityMode } from "@/pages/container-tabs/people/use-identity";
import { BrainDownBanner, BrainTabLanding } from "@/components/boo/BrainDown";
import { brainDown, brainStatusOf, createdLabel, healthPolling, lastAnsweredLabel, needsBrain, tabTooltip } from "@/lib/brain-state";

const TABS = [
  { value: "chat", label: "Chat" },
  { value: "logs", label: "Logs" },
  { value: "connections", label: "Connections" },
  { value: "integrations", label: "Integrations" },
  { value: "channels", label: "Channels" },
  { value: "people", label: "People" },
  { value: "extras", label: "Extras" },
  { value: "apps", label: "Apps" },
  { value: "models", label: "Models" },
  { value: "sharing", label: "Sharing" },
  { value: "access", label: "Access" },
  { value: "activity", label: "Activity" },
  { value: "usage", label: "Usage" },
  { value: "vault", label: "Vault" },
] as const;

type TabValue = (typeof TABS)[number]["value"];

// boo-brain has its own product surface (Slack/Teams/Google Chat). The
// dashboard surface for a brain is billing + wiring + vault management,
// so we hide everything except those. Kept at module scope so the
// redirect effect below has a stable reference and we don't trip the
// "more hooks than last render" rule by recomputing it after early returns.
const BRAIN_VISIBLE_TABS: ReadonlySet<TabValue> = new Set([
  "connections",
  "integrations",
  "channels",
  "people",
  "extras",
  "apps",
  "models",
  "sharing",
  "access",
  "activity",
  "usage",
  "vault",
]);

// The Chat tab is a two-way dashboard conversation with the brain — a
// super-admin-only surface (the backend routes are gated too), an internal
// tool for talking to a brain straight from the dashboard, never shown to
// customers.
//
// Brain is the only container type, so tab visibility is a fixed set with no
// per-type branching: everyone sees BRAIN_VISIBLE_TABS; super-admins with the
// dock's superadmin view switched on also see the Chat surface.
function isTabVisible(tab: TabValue, isSuperAdmin: boolean): boolean {
  if (tab === "chat") return isSuperAdmin;
  return BRAIN_VISIBLE_TABS.has(tab);
}

// The first tab in TABS order that's visible. Used as the bounce target when a
// user lands on a hidden tab — keeps the landing page tied to the visual tab
// order without a hardcoded fallback string.
function defaultTabFor(isSuperAdmin: boolean): TabValue {
  const first = TABS.find((t) => isTabVisible(t.value, isSuperAdmin));
  // TABS always has at least one visible entry: connections + integrations +
  // channels + apps + usage + vault (+ Chat for super-admins).
  return first?.value ?? "usage";
}

// Backend `gql` appends Railway's full error envelope as
// `Original message :: { path, extensions, others }` so support has the
// structured detail. End users don't want to read a JSON blob — show the
// pre-`::` prefix only. Fall back to a generic message when the row has
// no errorMessage at all (rare but possible).
function humanizeProvisioningError(raw: string | null | undefined): string {
  if (!raw) return "The container failed to start. Check the logs below for details.";
  const idx = raw.indexOf(" :: ");
  return idx === -1 ? raw : raw.slice(0, idx);
}

function activeTabFromPath(pathname: string, containerId: string): TabValue | null {
  // Match `…/containers/<id>` regardless of the org segment in front.
  // Connections is the container index (no tab segment), and a connection's
  // page sits at `…/connections/<id>`; every other tab is `…/<tab>`.
  const marker = `/containers/${containerId}`;
  const idx = pathname.indexOf(marker);
  if (idx === -1) return null;
  const rest = pathname.slice(idx + marker.length).replace(/^\//, "");
  const segment = rest.split("/")[0];
  if (!segment) return "connections";
  return (TABS.find((t) => t.value === segment)?.value ?? null);
}

// The page's path and its two old ones, which redirect to it.
function connectorSlugFromPath(pathname: string, containerId: string): string | null {
  for (const segment of ["custom", "proxies", "connectors"]) {
    const prefix = `/containers/${containerId}/integrations/${segment}/`;
    const at = pathname.indexOf(prefix);
    if (at === -1) continue;
    const slug = pathname.slice(at + prefix.length).replace(/\/$/, "");
    return slug && !slug.includes("/") ? decodeURIComponent(slug) : null;
  }
  return null;
}

export function ContainerLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const containerId = params.containerId;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isSuperAdmin = useSuperAdminView();

  // Collapse the main app sidebar while inside a container so the agent
  // gets a wide canvas. Re-expand on unmount (leaving the layout) so the
  // dashboard / other pages get their nav back.
  const { setOpen } = useSidebar();
  const setSidebarOpen = useEffectEvent(setOpen);
  useEffect(() => {
    setSidebarOpen(false);
    return () => setSidebarOpen(true);
  }, []);

  const { data: listResponse, isLoading, isError: isQueryError, error } = useQuery({
    queryKey: ["containers"],
    queryFn: api.containers.list,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      // Keep polling while anything is in flight: setup, a restart, or for
      // brain containers the per-org custom-domain ACME validation. The
      // dashboard gate below blocks the user inside ProvisioningView until
      // setup and the domain finish.
      const stillProvisioning = data.data.some(
        (r) =>
          r.deploymentStatus === "creating" ||
          r.deploymentStatus === "restarting" ||
          (r.agentType === "boo-brain" &&
            (r.customDomainStatus === "pending" ||
              r.customDomainStatus === "verifying")),
      );
      return stillProvisioning ? 5000 : false;
    },
  });
  const containers = listResponse?.data;

  const containerPreGuard = containers?.find((c) => c.id === containerId);

  // Whether the brain answers (ok, starting or offline), worked out by the
  // backend from the brain's own /healthz.
  const selfHosted = containerPreGuard?.selfHosted === true;
  const [brainIsDown, setBrainIsDown] = useState(false);
  const health = useQuery({
    queryKey: ["container-health", containerId],
    queryFn: () => api.containers.health(containerId!),
    enabled:
      !!containerId &&
      containerPreGuard?.deploymentStatus === "running" &&
      containerPreGuard?._permissions?.canView !== false,
    ...healthPolling({ selfHosted, down: brainIsDown }),
  });
  const healthErrorAt = health.errorUpdatedAt;
  const healthOkAt = health.dataUpdatedAt;

  // A Boo that answered during this visit only counts as down after several
  // misses in a row, so one blip never flips the page. The count advances
  // once per settled check (adjusted during render, keyed on the timestamps).
  const [healthFailures, setHealthFailures] = useState({ at: 0, count: 0 });
  const lastHealthCheckAt = Math.max(healthErrorAt, healthOkAt);
  if (lastHealthCheckAt !== healthFailures.at) {
    setHealthFailures({
      at: lastHealthCheckAt,
      count: healthErrorAt > healthOkAt ? healthFailures.count + 1 : 0,
    });
  }
  const brainStatus =
    containerPreGuard?.deploymentStatus === "running"
      ? brainStatusOf(healthOkAt >= healthErrorAt ? health.data : undefined, healthErrorAt > healthOkAt ? health.error : null)
      : null;
  const down = brainDown(brainStatus, { failures: healthFailures.count, answeredThisVisit: healthOkAt > 0 });
  // Boo Lab never starts a self-hosted Boo, so one that hasn't answered yet is
  // unreachable, not starting up.
  const observedDown = down && brainStatus && brainStatus.state !== "ok" ? brainStatus.state : null;
  const downState = selfHosted && observedDown ? "offline" : observedDown;
  if (down !== brainIsDown) setBrainIsDown(down);

  // Back up: the brain tabs reload in place, no refresh needed.
  const wasDown = useRef(false);
  useEffect(() => {
    if (wasDown.current && !down) void queryClient.invalidateQueries();
    wasDown.current = down;
  }, [down, queryClient]);

  // Last activity comes from the backend's container-activity rollup.
  const lastActivity = containerPreGuard?.lastActivityAt
    ? new Date(containerPreGuard.lastActivityAt).getTime()
    : null;

  const identityMode = useIdentityMode(containerId).mode;
  const legacyIdentity = identityMode === "legacy";
  // Extras is a conditional tab: it only earns a slot in the strip once the Boo
  // has thread-flag policies to manage. Same query key the People/Extras tabs
  // use, so it's deduped. A pre-v2 Boo 409s here — retry off, tab stays hidden.
  const { data: flagsData } = useQuery({
    queryKey: ["thread-flags", containerId],
    queryFn: () => api.containers.flags.list(containerId!),
    enabled:
      !!containerId &&
      legacyIdentity &&
      containerPreGuard?.deploymentStatus === "running" &&
      containerPreGuard?._permissions?.canConfigure !== false,
    retry: false,
  });
  const extrasVisible = (flagsData?.flags?.length ?? 0) > 0;

  // Dynamic page title: "<Tab>, <Boo name>" or just "<Boo name>"
  const titleTab = containerId ? activeTabFromPath(pathname, containerId) : null;
  const tabLabel = titleTab ? TABS.find((t) => t.value === titleTab)?.label : null;
  const containerTitle = containerPreGuard?.name;
  // The connection link Boo sends opens for anyone the org lets set up
  // connectors, Boo role or not. Without one they get the form alone (the
  // setup routes decide who may see it), never the Boo's header or tabs.
  const setupSlug = containerId ? connectorSlugFromPath(pathname, containerId) : null;
  const setupOnly =
    !!setupSlug && !!containers && (!containerPreGuard || containerPreGuard._permissions?.canView === false);
  useDocumentTitle(
    setupOnly
      ? undefined
      : containerTitle
      ? tabLabel && tabLabel !== "Connections"
        ? `${tabLabel}, ${containerTitle}`
        : containerTitle
      : null,
  );

  // If a deep link lands on a tab that's hidden for this viewer, bounce to a
  // visible one. Connections is the container index (bare route), so redirect
  // there when it's the fallback; other fallbacks (e.g. Chat for a super-admin)
  // are sub-paths. The bare container route renders Connections directly, so this
  // only fires for explicit hidden-tab links, never the plain `/…/$id` home.
  // Sits up here alongside the other hooks (not after the !container
  // early-return) so hook order stays stable — a useEffect below an early
  // return tripped React error #310 once the container row finished loading.
  useEffect(() => {
    if (!containerPreGuard || !containerId) return;
    const tab = activeTabFromPath(pathname, containerId);
    if (!tab) return;
    if (isTabVisible(tab, isSuperAdmin)) return;
    const params = { orgId: containerPreGuard.organizationId, containerId };
    const target = defaultTabFor(isSuperAdmin);
    if (target === "connections") {
      navigate({ to: "/orgs/$orgId/containers/$containerId", params, replace: true });
      return;
    }
    navigate({
      to: `/$orgId/containers/$containerId/${target}` as
        | "/orgs/$orgId/containers/$containerId/chat"
        | "/orgs/$orgId/containers/$containerId/logs"
        | "/orgs/$orgId/containers/$containerId/integrations"
        | "/orgs/$orgId/containers/$containerId/people"
        | "/orgs/$orgId/containers/$containerId/extras"
        | "/orgs/$orgId/containers/$containerId/apps"
        | "/orgs/$orgId/containers/$containerId/models"
        | "/orgs/$orgId/containers/$containerId/access"
        | "/orgs/$orgId/containers/$containerId/activity"
        | "/orgs/$orgId/containers/$containerId/usage"
        | "/orgs/$orgId/containers/$containerId/vault",
      params,
      replace: true,
    });
  }, [containerPreGuard, containerId, pathname, navigate, isSuperAdmin]);

  const startMutation = useMutation({
    mutationFn: () => api.containers.start(containerId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["containers"] }),
  });

  const stopMutation = useMutation({
    mutationFn: () => api.containers.stop(containerId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["containers"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.containers.delete(containerId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      navigate({ to: "/orgs/$orgId", params: { orgId: params.orgId ?? "" } });
    },
  });

  const tabsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    tabsRef.current
      ?.querySelector<HTMLElement>('[aria-current="page"]')
      ?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [pathname, isLoading, identityMode, extrasVisible]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [takingOver, setTakingOver] = useState(false);
  // Temporarily hidden per product decision — customers shouldn't be able to
  // pause or delete their Boo for now. Flip to true to restore both controls.
  const showLifecycleControls = false;
  // Super admins with the dock on can still pause a Boo.
  const showPause = showLifecycleControls || isSuperAdmin;

  if (isLoading) return <LoadingIndicator />;
  if (isQueryError || !containers) return <ErrorComponent error={error ?? "Failed to load Boos"} />;

  const container = containers.find((c) => c.id === containerId);
  if (setupOnly && setupSlug && containerId) return <ConnectorSetupPage containerId={containerId} slug={setupSlug} />;
  if (!container) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <ContainerIcon className="size-10 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Boo not found</h2>
        <p className="text-sm text-muted-foreground max-w-md">
          The URL points at a Boo that doesn&apos;t exist or you no longer
          have access to. It may have been deleted.
        </p>
        <code className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
          {containerId}
        </code>
        <Button asChild variant="outline">
          <Link to="/orgs/$orgId" params={{ orgId: params.orgId ?? "" }}>Back to Dashboard</Link>
        </Button>
      </div>
    );
  }

  // Inside a Boo its own permissions decide (Boo role, or org role for fleet
  // actions like start/stop); the list's org-level hints only fill gaps.
  const permissions = containerPermissions(container, listResponse?._permissions);
  const booPerms = container._permissions;
  // Someone with no role in this Boo sees who owns it, not its tabs; org
  // owners, admins and billing admins also see its spend.
  const noAccess = booPerms !== undefined && !booPerms.canView;
  // Org owners who don't own the Boo; kept in the header menu, out of the way.
  const canTakeOver = booPerms?.canTakeOwnership === true && booPerms.canManageAdmins !== true;
  const deploymentStatus = container.deploymentStatus;
  const isRunning = deploymentStatus === "running";
  const isStopped = deploymentStatus === "stopped";
  const isRestarting = deploymentStatus === "restarting";
  // For boo-brain we don't release the dashboard until the per-org custom
  // domain has finished ACME validation. The .up.railway.app URL is up
  // earlier, but the dashboard surfaces the branded URL to the customer
  // (Google Chat events URL, Slack events URL, etc.) — letting them in
  // before customDomainStatus === "active" means they paste a not-yet-valid
  // URL into Google Cloud Console and Boo silently fails to respond.
  // `error` unblocks the dashboard so the user isn't stuck if ACME truly
  // fails; deploymentStatus === "creating" still gates separately. A Boo
  // that failed before it ever ran shows the setup failure.
  const isDomainProvisioning =
    container.agentType === "boo-brain" &&
    (container.customDomainStatus === "pending" ||
      container.customDomainStatus === "verifying");
  const isCreating =
    deploymentStatus === "creating" ||
    (deploymentStatus === "error" && !container.readyAt && !lastActivity) ||
    isDomainProvisioning;
  const isError = deploymentStatus === "error";
  const busy =
    startMutation.isPending || stopMutation.isPending || deleteMutation.isPending;

  const activeTab = activeTabFromPath(pathname, container.id);

  // Visible tab strip — must mirror the hidden-tab rule used by the redirect
  // effect at the top of the component (isTabVisible), otherwise a brain
  // user could navigate via the strip to a hidden tab and get bounced
  // mid-render. Derived inline here (not via a hook) so it can sit after
  // the early returns without breaking hook order.
  // Extras additionally gates on having thread-flags to manage — a normal deep
  // link to /extras still renders (isTabVisible keeps it routable), it just
  // doesn't clutter the strip until there's something there.
  const visibleTabs = TABS.filter(
    (t) =>
      isTabVisible(t.value, isSuperAdmin) &&
      (t.value !== "extras" || (legacyIdentity && extrasVisible)) &&
      (t.value !== "access" || legacyIdentity),
  );

  return (
    <div className="flex flex-1 flex-col min-h-0">
      {/* Header: back button + name + status (left), action buttons (right).
          The last-activity hint sits directly under the name. */}
      <div className="flex items-start justify-between gap-4 px-6 py-4">
        <div className="flex items-start gap-3 min-w-0">
          <Button asChild variant="ghost" size="icon" className="shrink-0 mt-0.5 bg-muted hover:bg-muted/70" title="Back to Boos">
            <Link to="/orgs/$orgId" params={{ orgId: container.organizationId }}>
              <ArrowLeft className="size-4" />
              <span className="sr-only">Back to Boos</span>
            </Link>
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <BooNameEditor
                containerId={container.id}
                name={container.name}
                canRename={booPerms?.canConfigure === true}
              />
              {container.posture === "chief-of-staff" && (
                <span className="shrink-0 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">chief of staff</span>
              )}
              <StatusBadge
                status={downState ? `brain-${downState}` : deploymentStatus}
                errorMessage={container.errorMessage}
              />
              {container.selfHosted && (
                <span
                  className="shrink-0 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded"
                  title="Runs on your own machine"
                >
                  Self-hosted
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {downState === "starting"
                ? createdLabel(container.createdAt)
                : (downState === "offline" && lastAnsweredLabel(brainStatus?.lastSeenAt ?? null)) ||
                  (lastActivity ? `Active ${relativeTime(lastActivity)} ago` : "No activity yet")}
            </p>
          </div>
        </div>
        {(!isCreating || deploymentStatus === "error") && (
          <div className="flex items-center gap-2 shrink-0">
            {showPause && isRunning && permissions?.canSuspend === true && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => stopMutation.mutate()}
                title="Pause Boo"
              >
                {stopMutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Square className="size-3.5" />
                )}
                Pause
              </Button>
            )}
            {(isStopped || isError) && permissions?.canResume === true && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => startMutation.mutate()}
                title="Resume Boo"
              >
                {startMutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Play className="size-3.5" />
                )}
                Resume
              </Button>
            )}
            {showLifecycleControls && permissions?.canSoftDelete === true && (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              disabled={busy}
              onClick={() => setDeleteOpen(true)}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Trash2 className="size-3.5" />
              )}
              Delete
            </Button>
            )}
            {canTakeOver && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="size-8" aria-label="More actions">
                    <EllipsisVertical className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onSelect={() => setTakingOver(true)}>
                    <KeyRound />
                    Take ownership
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <ConfirmDialog
              open={deleteOpen}
              onOpenChange={setDeleteOpen}
              title={`Delete "${container.name}"?`}
              description="This deactivates the Boo and destroys its infrastructure. Usage history is preserved."
              confirmLabel="Delete"
              confirmPhrase={container.name}
              loading={deleteMutation.isPending}
              onConfirm={() => deleteMutation.mutate()}
            />
          </div>
        )}
      </div>

      {canTakeOver && (
        <TakeOwnershipDialog
          containerId={container.id}
          booName={container.name}
          open={takingOver}
          onOpenChange={setTakingOver}
        />
      )}
      {noAccess ? (
        booPerms?.canViewSpend ? (
          <>
            <BooSpendOnlyNotice container={container} />
            <UsageTab ledgerOnly />
          </>
        ) : (
          <BooNoAccess container={container} />
        )
      ) : isCreating ? (
        <ProvisioningView
          container={container}
          failed={deploymentStatus === "error"}
          errorMessage={container.errorMessage}
        />
      ) : (
        <>
          {isRestarting && (
            <div
              role="status"
              className="mx-6 mt-2 flex items-center gap-2 rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200"
            >
              <Loader2 className="size-4 shrink-0 animate-spin motion-reduce:animate-none" />
              <span className="flex-1">{container.name} is restarting. Chat may not answer for a moment.</span>
            </div>
          )}
          {downState && (
            <BrainDownBanner
              state={downState}
              name={container.name}
              since={Date.parse(container.readyAt ?? container.createdAt)}
              retrying={health.isFetching}
              onRetry={() => void health.refetch()}
              selfHosted={container.selfHosted === true}
            />
          )}
          <AccessNotices containerId={container.id} canDismiss={booPerms?.canManageMembers === true} />
          <nav
            ref={tabsRef}
            className="flex gap-1 overflow-x-auto whitespace-nowrap border-b px-6 [scrollbar-width:none] [mask-image:linear-gradient(to_right,transparent,#000_1.5rem,#000_calc(100%-1.5rem),transparent)] sm:[mask-image:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Boo sections"
          >
            {visibleTabs.map((t) => {
              const isActive = activeTab === t.value;
              // Greyed while the brain is down; a link to it still lands on a calm panel.
              const unavailable = downState !== null && needsBrain(t.value);
              return (
                <button
                  key={t.value}
                  type="button"
                  aria-current={isActive ? "page" : undefined}
                  aria-disabled={unavailable || undefined}
                  title={unavailable && downState ? tabTooltip(downState, container.name) : undefined}
                  onClick={() => {
                    if (unavailable) return;
                    const params = { orgId: container.organizationId, containerId: container.id };
                    // Connections is the container index (bare route); others are sub-paths.
                    if (t.value === "connections") {
                      navigate({ to: "/orgs/$orgId/containers/$containerId", params });
                      return;
                    }
                    navigate({
                      to: `/$orgId/containers/$containerId/${t.value}` as
                        | "/orgs/$orgId/containers/$containerId/chat"
                        | "/orgs/$orgId/containers/$containerId/logs"
                        | "/orgs/$orgId/containers/$containerId/integrations"
                        | "/orgs/$orgId/containers/$containerId/people"
                        | "/orgs/$orgId/containers/$containerId/extras"
                        | "/orgs/$orgId/containers/$containerId/apps"
                        | "/orgs/$orgId/containers/$containerId/models"
                        | "/orgs/$orgId/containers/$containerId/access"
                        | "/orgs/$orgId/containers/$containerId/activity"
                        | "/orgs/$orgId/containers/$containerId/usage"
                        | "/orgs/$orgId/containers/$containerId/vault",
                      params,
                    });
                  }}
                  className={`shrink-0 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                    unavailable
                      ? `cursor-not-allowed text-muted-foreground/50 ${isActive ? "border-border font-medium" : "border-transparent"}`
                      : isActive
                      ? "border-foreground text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </nav>

          <div className="flex flex-1 flex-col min-h-0">
            {downState && activeTab && needsBrain(activeTab) ? (
              <BrainTabLanding
                tab={activeTab}
                label={TABS.find((t) => t.value === activeTab)?.label ?? ""}
                state={downState}
                name={container.name}
                orgId={container.organizationId}
                containerId={container.id}
              />
            ) : (
              <Outlet />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ProvisioningView({
  container,
  failed,
  errorMessage,
}: {
  container: import("@/lib/api").ContainerRow;
  failed?: boolean;
  errorMessage?: string | null;
}) {
  const conditions = useSetupConditions(container.id, !failed);
  const isSuperAdmin = useSuperAdminView();
  if (failed) {
    return (
      <div className="flex flex-1 flex-col min-h-0 p-6 gap-5">
        <div className="flex items-center gap-3">
          <div className="size-4 rounded-full bg-destructive/20 flex items-center justify-center">
            <span className="text-destructive text-xs font-bold">!</span>
          </div>
          <div>
            <p className="text-sm font-medium">Deployment failed</p>
            <p className="text-xs text-muted-foreground">
              {humanizeProvisioningError(errorMessage)}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col min-h-0 p-6">
      <BrainProvisioningStatus botName={container.name?.trim() || "Boo"} conditions={conditions} showDetails={isSuperAdmin} />
    </div>
  );
}
